import { useEffect, useState } from 'react'
import { useUIStore } from '@/store/useUIStore'
import { useCurrentConversationStore } from '@/store/useCurrentConversationStore'

/**
 * Syncs the theme from the UI store to the <html> element.
 * Handles 'light', 'dark', and 'system' modes, including system preference changes.
 *
 * @returns {{
 *   mode: 'light' | 'dark' | 'system',
 *   effectiveTheme: 'light' | 'dark',
 *   isDark: boolean
 * }}
 */
export function useThemeSync() {
  const mode = useUIStore(state => state.theme) // 'light' | 'dark' | 'system'
  const isNewChat = useCurrentConversationStore(state => state.id) === null
  const [effectiveTheme, setEffectiveTheme] = useState('light') // actual applied theme

  useEffect(() => {
    if (typeof window === 'undefined') return

    const root = document.documentElement
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

    // New-chat landing should always be dark mode (even if user prefers light/system).
    if (isNewChat) {
      root.classList.add('dark')
      setEffectiveTheme('dark')
      return
    }

    /**
     * Apply dark/light based on whether dark is active.
     * @param {boolean} matches
     */
    const applyFromMatch = matches => {
      if (matches) {
        root.classList.add('dark')
        setEffectiveTheme('dark')
      } else {
        root.classList.remove('dark')
        setEffectiveTheme('light')
      }
    }

    /**
     * Apply theme based on current mode.
     * @param {'light' | 'dark' | 'system'} t
     */
    const applyTheme = t => {
      if (t === 'dark') {
        applyFromMatch(true)
      } else if (t === 'light') {
        applyFromMatch(false)
      } else {
        applyFromMatch(mediaQuery.matches)
      }
    }

    applyTheme(mode)

    if (mode !== 'system') {
      return
    }

    /**
     * @param {MediaQueryListEvent} e
     */
    const handler = e => {
      applyFromMatch(e.matches)
    }

    mediaQuery.addEventListener('change', handler)
    return () => {
      mediaQuery.removeEventListener('change', handler)
    }
  }, [mode, isNewChat])

  return {
    mode,
    effectiveTheme,
    isDark: effectiveTheme === 'dark',
  }
}
